import {
  Check,
  Comment as CommentIcon,
  PriorityHigh,
} from '@mui/icons-material';
import { IconButton, Theme, useTheme } from '@mui/material';
import { Box, darken } from '@mui/system';
import { CommentStatus } from 'api/models/Comment';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { dialogState } from 'state';
import { commentsByFieldTemplateId } from 'state/formState';
import { DialogTypes, GeneralIds } from 'types/types';

export function getCommentIconColor(status: CommentStatus, theme: Theme) {
  switch (status) {
    case CommentStatus.unchecked:
      return theme.palette.grey[500];
    case CommentStatus.notchecked:
      return theme.palette.warning.main;
    case CommentStatus.closed:
    case CommentStatus.checked:
      return theme.palette.success.main;
    case CommentStatus.open:
      return theme.palette.error.main;
    case CommentStatus.answered:
    default:
      return theme.palette.primary.main;
  }
}

export function getCommentIcon({
  status,
  size = 'inherit',
  color = 'white',
}: {
  status: CommentStatus;
  size?: 'small' | 'inherit' | 'medium';
  color?: 'white' | 'black';
}) {
  switch (status) {
    case CommentStatus.notchecked:
      return <PriorityHigh fontSize={size} sx={{ color }} />;
    case CommentStatus.checked:
      return <Check fontSize={size} sx={{ color }} />;
    case CommentStatus.open:
    case CommentStatus.unchecked:
    case CommentStatus.answered:
    case CommentStatus.closed:
    default:
      return <CommentIcon fontSize={size} sx={{ color }} />;
  }
}

const DEFAULT_COMMENT = { status: CommentStatus.unchecked };

interface CommentProps {
  fieldId: string;
  value: unknown;
}

export default function CommentButton({ fieldId, value }: CommentProps) {
  const { formId } = useParams<GeneralIds>();
  const openDialog = useSetRecoilState(dialogState);
  const comments = useRecoilValue(commentsByFieldTemplateId(fieldId));
  const theme = useTheme();

  const comment = comments[0] ?? DEFAULT_COMMENT;

  const onOpenDialog = () => {
    openDialog({
      dialogType: DialogTypes.comment,
      isOpen: true,
      data: {
        comments,
        value,
        fieldId,
        formId,
      },
    });
  };

  return (
    <Box sx={{ width: '50px', ml: 1 }}>
      <IconButton
        color="default"
        onClick={onOpenDialog}
        sx={{
          backgroundColor: getCommentIconColor(comment.status, theme),
          '&:hover': {
            background: darken(getCommentIconColor(comment.status, theme), 0.4),
          },
        }}
      >
        {getCommentIcon({ status: comment.status })}
      </IconButton>
    </Box>
  );
}
